import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { CustomPaginationProps } from './types/CustomPaginationType';
import './CustomPagination.css';

/**
 * CustomPagination Component
 * A responsive, accessible enterprise pagination control.
 */
export const CustomPagination: React.FC<CustomPaginationProps> = ({
    currentPage,
    totalPages,
    totalCount,
    pageSize = 10,
    onPageChange,
    hasPrevious,
    hasNext,
    isLoading = false,
    pageSizeOptions,
    onPageSizeChange,
    itemName = 'records',
    className = '',
    showPageNumbers = true,
}) => {
    // Normalise pages
    const effectiveTotalPages = Math.max(1, totalPages || 1);
    const validCurrentPage = Math.min(Math.max(1, currentPage || 1), effectiveTotalPages);

    // Compute Previous / Next enabled status
    const isPrevDisabled = isLoading || (hasPrevious !== undefined ? !hasPrevious : validCurrentPage <= 1);
    const isNextDisabled = isLoading || (hasNext !== undefined ? !hasNext : validCurrentPage >= effectiveTotalPages);

    // Generate page numbers array with ellipses
    const getPageNumbers = (): (number | string)[] => {
        if (effectiveTotalPages <= 7) {
            return Array.from({ length: effectiveTotalPages }, (_, i) => i + 1);
        }

        const pages: (number | string)[] = [];
        const leftSibling = Math.max(validCurrentPage - 1, 1);
        const rightSibling = Math.min(validCurrentPage + 1, effectiveTotalPages);

        const showLeftDots = leftSibling > 2;
        const showRightDots = rightSibling < effectiveTotalPages - 1;

        if (!showLeftDots && showRightDots) {
            const leftItemCount = 3 + 2;
            const leftRange = Array.from({ length: leftItemCount }, (_, i) => i + 1);
            return [...leftRange, '...', effectiveTotalPages];
        }

        if (showLeftDots && !showRightDots) {
            const rightItemCount = 3 + 2;
            const rightRange = Array.from(
                { length: rightItemCount },
                (_, i) => effectiveTotalPages - rightItemCount + 1 + i
            );
            return [1, '...', ...rightRange];
        }

        if (showLeftDots && showRightDots) {
            const middleRange = [leftSibling, validCurrentPage, rightSibling];
            return [1, '...', ...middleRange, '...', effectiveTotalPages];
        }

        return Array.from({ length: effectiveTotalPages }, (_, i) => i + 1);
    };

    // Calculate item ranges for summary
    const calculateRange = () => {
        if (totalCount === undefined || totalCount === null) {
            return `Page ${validCurrentPage} of ${effectiveTotalPages}`;
        }
        if (totalCount === 0) {
            return `No ${itemName} found`;
        }
        const start = (validCurrentPage - 1) * pageSize + 1;
        const end = Math.min(validCurrentPage * pageSize, totalCount);
        return (
            <>
                Showing <span className="custom-pagination__info-count">{start}–{end}</span> of{' '}
                <span className="custom-pagination__info-count">{totalCount}</span> {itemName}
            </>
        );
    };

    return (
        <div className={`custom-pagination ${className}`} aria-label="Pagination">
            <div className="custom-pagination__info">
                {calculateRange()}
                {pageSizeOptions && pageSizeOptions.length > 0 && onPageSizeChange && (
                    <div className="custom-pagination__size-selector">
                        <span>Show</span>
                        <select
                            className="custom-pagination__size-select"
                            value={pageSize}
                            onChange={(e) => onPageSizeChange(Number(e.target.value))}
                            disabled={isLoading}
                            aria-label="Rows per page"
                        >
                            {pageSizeOptions.map((size) => (
                                <option key={size} value={size}>
                                    {size} / page
                                </option>
                            ))}
                        </select>
                    </div>
                )}
            </div>

            <div className="custom-pagination__controls">
                <button
                    type="button"
                    className="custom-pagination__btn"
                    onClick={() => onPageChange(validCurrentPage - 1)}
                    disabled={isPrevDisabled}
                    aria-label="Previous Page"
                >
                    <ChevronLeft size={16} />
                    <span className="custom-pagination__btn-text">Previous</span>
                </button>

                <span className="custom-pagination__page-indicator">
                    Page {validCurrentPage} of {effectiveTotalPages}
                </span>

                {showPageNumbers && (
                    <div className="custom-pagination__pages" role="navigation" aria-label="Page navigation">
                        {getPageNumbers().map((p, idx) => {
                            if (p === '...') {
                                return (
                                    <span key={`dots-${idx}`} className="custom-pagination__ellipsis">
                                        •••
                                    </span>
                                );
                            }
                            const pageNum = Number(p);
                            const isActive = pageNum === validCurrentPage;
                            return (
                                <button
                                    key={`page-${pageNum}`}
                                    type="button"
                                    className={`custom-pagination__page-btn ${isActive ? 'active' : ''}`}
                                    onClick={() => onPageChange(pageNum)}
                                    disabled={isLoading}
                                    aria-current={isActive ? 'page' : undefined}
                                    aria-label={`Page ${pageNum}`}
                                >
                                    {pageNum}
                                </button>
                            );
                        })}
                    </div>
                )}

                <button
                    type="button"
                    className="custom-pagination__btn"
                    onClick={() => onPageChange(validCurrentPage + 1)}
                    disabled={isNextDisabled}
                    aria-label="Next Page"
                >
                    <span className="custom-pagination__btn-text">Next</span>
                    <ChevronRight size={16} />
                </button>
            </div>
        </div>
    );
};

export default CustomPagination;
